(() => {
  const controllers = new Map();

  function mount(wrap) {
    if (wrap.dataset.videoMounted) return;
    const hero = wrap.closest('.hero');
    const video = wrap.querySelector('video');
    if (!hero || !video) return;
    wrap.dataset.videoMounted = 'true';
    const abort = new AbortController();
    const options = { signal: abort.signal };
    const passive = { ...options, passive: true };
    const reduce = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 749px)');
    const header = document.querySelector('header-component');
    const heading = hero.querySelector('.hero__content-wrapper h1, .hero__content-wrapper h2');
    const offer = hero.querySelector('[data-ws-home-offer]');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ws-hero-video__toggle';
    button.hidden = true;
    hero.appendChild(button);
    video.muted = true;
    let wantsPlayback = !reduce.matches;
    let manuallyPaused = false;
    let restoredTime = 0;
    let currentSource = '';
    let pendingMetadata = null;
    let failed = false;
    let fitFrame = 0;
    let pointerStart = null;
    let dragged = false;

    const sync = () => {
      button.setAttribute('aria-label', video.paused ? 'Play background video' : 'Pause background video');
    };
    const playIfWanted = () => {
      if (wantsPlayback && !document.hidden) video.play().catch(sync);
      else video.pause();
      sync();
    };
    const visibleRect = element => {
      if (!element || element.hidden || !element.getClientRects().length) return null;
      const style = getComputedStyle(element);
      return style.visibility === 'hidden' || style.display === 'none' ? null : element.getBoundingClientRect();
    };
    const fit = () => {
      fitFrame = 0;
      if (!hero.isConnected) return;
      const headerRect = visibleRect(header);
      wrap.style.setProperty('--ws-video-top', `${headerRect?.height || 0}px`);
      const rect = hero.getBoundingClientRect();
      const titleRect = visibleRect(heading);
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportBottom = viewportTop + (viewport?.height || innerHeight);
      let top = Math.max(rect.top, viewportTop, headerRect?.bottom || 0);
      // Include an expanded details panel, which may extend outside the offer row.
      if (offer) {
        for (const element of [offer, ...offer.querySelectorAll('.ws-home-offer__row,.ws-home-offer__panel')]) {
          const offerRect = visibleRect(element);
          if (offerRect && offerRect.bottom > top && offerRect.top < viewportBottom) top = offerRect.bottom;
        }
      }
      const bottom = Math.min(titleRect?.top ?? top, rect.bottom, viewportBottom);
      const height = Math.max(0, bottom - top);
      button.style.setProperty('--ws-video-hit-top', `${Math.max(0, top - rect.top)}px`);
      button.style.setProperty('--ws-video-hit-height', `${height}px`);
      button.hidden = failed || !titleRect || height < 1;
    };
    const scheduleFit = () => { if (!fitFrame) fitFrame = requestAnimationFrame(fit); };
    fit();
    const observer = new ResizeObserver(scheduleFit);
    for (const element of [hero, header, heading, offer, ...(offer?.querySelectorAll('.ws-home-offer__row,.ws-home-offer__panel') || [])]) {
      if (element) observer.observe(element);
    }
    const offerObserver = new MutationObserver(scheduleFit);
    if (offer) offerObserver.observe(offer, { subtree: true, attributes: true, childList: true, attributeFilter: ['hidden', 'class', 'style', 'aria-expanded'] });
    document.addEventListener('scroll', scheduleFit, { ...passive, capture: true });
    window.addEventListener('resize', scheduleFit, passive);
    window.addEventListener('pageshow', scheduleFit, passive);
    window.visualViewport?.addEventListener('resize', scheduleFit, passive);
    window.visualViewport?.addEventListener('scroll', scheduleFit, passive);
    document.fonts?.ready.then(() => { if (!abort.signal.aborted) scheduleFit(); });

    function setSource() {
      video.poster = mobile.matches ? wrap.dataset.mobilePoster : wrap.dataset.desktopPoster;
      const source = mobile.matches ? wrap.dataset.mobileSrc : wrap.dataset.desktopSrc;
      // Reduced-motion visitors see the poster until they explicitly choose playback.
      if (!currentSource && !wantsPlayback) return;
      if (currentSource === source) return;
      if (video.readyState > 0 && Number.isFinite(video.currentTime)) restoredTime = video.currentTime;
      if (pendingMetadata) video.removeEventListener('loadedmetadata', pendingMetadata);
      currentSource = source;
      failed = false;
      video.pause();
      video.classList.remove('is-playing');
      pendingMetadata = () => {
        const maximum = Number.isFinite(video.duration) ? Math.max(0, video.duration - 0.04) : 0;
        video.currentTime = Math.min(restoredTime, maximum);
        playIfWanted();
      };
      video.addEventListener('loadedmetadata', pendingMetadata, { ...options, once: true });
      video.src = source;
      video.preload = 'metadata';
      video.load();
      scheduleFit();
      sync();
    }
    video.addEventListener('playing', () => { video.classList.add('is-playing'); sync(); }, options);
    video.addEventListener('seeked', () => {
      if (restoredTime > 0 && video.readyState >= 2) video.classList.add('is-playing');
    }, options);
    video.addEventListener('pause', sync, options);
    video.addEventListener('error', () => { failed = true; video.classList.remove('is-playing'); button.hidden = true; }, options);
    // Native pan/zoom stays enabled. A drag or scroll is never a playback tap.
    button.addEventListener('pointerdown', event => {
      dragged = !event.isPrimary || event.button !== 0;
      pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY, scroll: window.scrollY };
    }, passive);
    button.addEventListener('pointermove', event => {
      if (pointerStart && event.pointerId === pointerStart.id && Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > 10) dragged = true;
    }, passive);
    button.addEventListener('pointercancel', () => { dragged = true; pointerStart = null; }, passive);
    button.addEventListener('click', event => {
      if (event.detail !== 0 && (dragged || (pointerStart && Math.abs(window.scrollY - pointerStart.scroll) > 4))) return;
      pointerStart = null;
      dragged = false;
      manuallyPaused = !video.paused;
      wantsPlayback = !manuallyPaused;
      if (wantsPlayback) setSource();
      playIfWanted();
    }, options);
    document.addEventListener('visibilitychange', playIfWanted, options);
    reduce.addEventListener('change', () => {
      wantsPlayback = !reduce.matches && !manuallyPaused;
      setSource();
      playIfWanted();
    }, options);
    mobile.addEventListener('change', () => { setSource(); scheduleFit(); }, options);
    setSource();
    sync();
    controllers.set(wrap, () => {
      abort.abort();
      observer.disconnect();
      offerObserver.disconnect();
      cancelAnimationFrame(fitFrame);
      video.pause();
      button.remove();
      delete wrap.dataset.videoMounted;
      controllers.delete(wrap);
    });
  }

  document.querySelectorAll('[data-ws-hero-video]').forEach(mount);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-ws-hero-video]').forEach(mount));
  document.addEventListener('shopify:section:unload', event => {
    for (const [wrap, dispose] of controllers) if (event.target.contains(wrap)) dispose();
  });
})();
